import { Outlet } from 'react-router-dom';
import { lazy, Suspense } from 'react';

// ═══════════════════════════════════════════════════════════════════════════════
// CRITICAL PERFORMANCE: Lazy load layout components
// MainNavigation pulls in @headlessui/react (~15KB) + @heroicons/react (~20KB)
// Footer and Newsletter add more weight - all deferred to after first paint
// ═══════════════════════════════════════════════════════════════════════════════
const MainNavigation = lazy(() => import('./MainNavigation').then(m => ({ default: m.MainNavigation })));
const NewsletterSubscription = lazy(() => import('./NewsletterSubscription').then(m => ({ default: m.NewsletterSubscription })));
const Footer = lazy(() => import('./Footer').then(m => ({ default: m.Footer })));
const PostLoginChecker = lazy(() => import('@/core/components/PostLoginChecker').then(m => ({ default: m.PostLoginChecker })));

// Minimal skeleton for navigation while loading
function NavSkeleton() {
  return (
    <header className="h-16 bg-white dark:bg-secondary-900 border-b border-secondary-200 dark:border-white/10">
      <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
        <div className="w-24 h-8 bg-secondary-200 dark:bg-white/10 rounded animate-pulse" />
        <div className="flex gap-4">
          <div className="w-16 h-4 bg-secondary-200 dark:bg-white/10 rounded animate-pulse" />
          <div className="w-16 h-4 bg-secondary-200 dark:bg-white/10 rounded animate-pulse" />
        </div>
      </div>
    </header>
  );
}

/**
 * PublicLayout - Layout para páginas públicas (no admin)
 * 
 * Incluye:
 * - MainNavigation (header con mega menú) - LAZY LOADED
 * - Outlet para contenido de la ruta
 * - NewsletterSubscription (antes del footer) - LAZY LOADED
 * - Footer (pie de página) - LAZY LOADED
 * 
 * Uso:
 * En el router, usar como elemento padre para rutas públicas
 */
export function PublicLayout() {
  return (
    <div data-theme="user" className="min-h-screen bg-white dark:bg-black">
      <Suspense fallback={<NavSkeleton />}>
        <MainNavigation />
      </Suspense>
      <main className="pb-20 md:pb-0">
        <Outlet />
      </main>
      <Suspense fallback={null}>
        <NewsletterSubscription />
      </Suspense>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
      {/* Post-login checks (passkey promotion, terms acceptance) */}
      <Suspense fallback={null}>
        <PostLoginChecker />
      </Suspense>
    </div>
  );
}

